import React, { useState } from "react";
import { Grid, Button, CircularProgress, Stack, TextField } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EmailIcon from '@mui/icons-material/Email';
import { api } from "../../helpers/apiRequest";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import colors from "../../theme/Colors";
import { exportToPDF, exportToExcel, getPDFBlob } from "./ReportUtils";
import EmailReportDialog from "../EmailReportDialog";

const OrderProgressReport = ({ customers }) => {
  const [loadingReport, setLoadingReport] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedEmailCustomer, setSelectedEmailCustomer] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [filters, setFilters] = useState({
    poNumber: "",
  });

  const generateReport = async (exportType = 'html') => {
    if (!filters.poNumber) {
      toast.error("Please enter a PO Number");
      return;
    }

    const params = new URLSearchParams();
    params.append('poNumber', filters.poNumber);
    
    setLoadingReport(true);
    try {
      const response = await api.get(
        `/reports/order-progress-report?${params.toString()}`
      );
      
      if (response.data) {
        if (exportType === 'excel') {
          const excelData = [{
            "PO Number": response.data.data.poNumber,
            "Customer": response.data.data.customer.name,
            "Item": response.data.data.item,
            "Style": response.data.data.pStyle,
            "PO Date": dayjs(response.data.data.poDate).format('YYYY-MM-DD'),
            "Ex-Factory Date": dayjs(response.data.data.exFactoryDate).format('YYYY-MM-DD'),
            "PO Quantity": response.data.data.poQuantity,
            "Shipped Quantity 1": response.data.data.shippedQuantity,
            "Lot Date 1": response.data.data.lotDate1 ? dayjs(response.data.data.lotDate1).format('YYYY-MM-DD') : '',
            "Shipped Quantity 2": response.data.data.shippedQuantity2,
            "Lot Date 2": response.data.data.lotDate2 ? dayjs(response.data.data.lotDate2).format('YYYY-MM-DD') : '',
            "Balance Quantity": response.data.data.balanceQuantity,
            "Achievement": response.data.data.achievement,
            "Achievement On Time": response.data.data.achievementOnTime,
            "Note": response.data.data.note
          }];
          
          exportToExcel(excelData, `Order_Progress_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("Excel report generated successfully");
          return;
        }
        
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Order Progress Report</title>
            <style>
               body { font-family: Arial, sans-serif; margin: 15px; padding: 50px 30px; align-items:center;-webkit-user-select: text;-moz-user-select: text;-ms-user-select: text; user-select: text;  }
             h1 { color: #1abc9c; text-align: center; }
              table { border-collapse: collapse; width: 95%; margin: 15px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; }
              .report-filters { margin: 15px; font-size: 14px; }
              .achievement-achieved { color: green; }
              .achievement-not-achieved { color: red; }
              .print-btn { background-color: #1abc9c; color: white; border: none; padding: 10px 20px; 
                          cursor: pointer; border-radius: 4px; margin-top: 20px; margin-right: 10px; }
              .export-btn-container { display: flex; margin-top: 20px; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Order Progress Report</h1>
              <div class="report-filters">
                <p><strong>PO Number:</strong> ${filters.poNumber}</p>
                 <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
              </div>
             
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>PO Number</th>
                  <th>Customer</th>
                  <th>Item</th>
                  <th>Style</th>
                  <th>PO Date</th>
                  <th>Ex-Factory Date</th>
                  <th>PO Quantity</th>
                  <th>Shipped Qty 1</th>
                  <th>Lot Date 1</th>
                  <th>Shipped Qty 2</th>
                  <th>Lot Date 2</th>
                  <th>Balance Qty</th>
                  <th>Achievement</th>
                  <th>Achievement On Time</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
        `;
        
        const achievementClass = response.data.data.achievement === 'ACHIEVED' ? 'achievement-achieved' : 'achievement-not-achieved';
        
        htmlContent += `
          <tr>
            <td>${response.data.data.poNumber}</td>
            <td>${response.data.data.customer.name}</td>
            <td>${response.data.data.item}</td>
            <td>${response.data.data.pStyle}</td>
            <td>${dayjs(response.data.data.poDate).format('YYYY-MM-DD')}</td>
            <td>${dayjs(response.data.data.exFactoryDate).format('YYYY-MM-DD')}</td>
            <td>${response.data.data.poQuantity}</td>
            <td>${response.data.data.shippedQuantity}</td>
            <td>${response.data.data.lotDate1 ? dayjs(response.data.data.lotDate1).format('YYYY-MM-DD') : ''}</td>
            <td>${response.data.data.shippedQuantity2}</td>
            <td>${response.data.data.lotDate2 ? dayjs(response.data.data.lotDate2).format('YYYY-MM-DD') : ''}</td>
            <td>${response.data.data.balanceQuantity}</td>
            <td class="${achievementClass}">${response.data.data.achievement}</td>
            <td>${response.data.data.achievementOnTime}</td>
            <td>${response.data.data.note}</td>
          </tr>
        `;
        
        htmlContent += `
              </tbody>
            </table>
            
            <div class="export-btn-container">
            </div>
          </body>
          </html>
        `;
        
        if (exportType === 'pdf') {
          exportToPDF(htmlContent, `Order_Progress_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}`);
          toast.success("PDF report generated successfully");
          return;
        }
        
        if (exportType === 'email') {
          setEmailDialogOpen(true);
          setLoadingReport(false);
          return;
        }
        
        const reportWindow = window.open('', '_blank');
        if (!reportWindow) {
          toast.error("Please allow popups for this site to view reports");
          return;
        }
        
        reportWindow.document.write(htmlContent);
        reportWindow.document.close();
        
        window.addEventListener('message', function(event) {
          if (event.data.type === 'exportPDF' && event.data.reportType === 'orderProgress') {
            generateReport('pdf');
          } else if (event.data.type === 'exportExcel' && event.data.reportType === 'orderProgress') {
            generateReport('excel');
          }
        });
        
        toast.success("Report generated successfully");
      } else {
        toast.error("No data available for the selected PO Number");
      }
    } catch (error) {
      console.error("Error generating order progress report:", error);
      toast.error("Failed to generate order progress report");
    } finally {
      setLoadingReport(false);
    }
  };

  const sendReportByEmail = async () => {
    if (!selectedEmailCustomer) {
      toast.error('Please select a customer');
      return;
    }

    if (!filters.poNumber) {
      toast.error("Please enter a PO Number");
      return;
    }

    setSendingEmail(true);
    try {
      // Generate the report HTML
      const params = new URLSearchParams();
      params.append('poNumber', filters.poNumber);
      
      const response = await api.get(`/reports/order-progress-report?${params.toString()}`);
      
      if (response.data) {
        let htmlContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <title>Order Progress Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 15px; padding: 50px 30px; align-items:center;-webkit-user-select: text;-moz-user-select: text;-ms-user-select: text; user-select: text;  }
             h1 { color: #1abc9c; text-align: center; }
              table { border-collapse: collapse; width: 95%; margin: 15px; }
              th { background-color: #1abc9c; color: white; text-align: left; padding: 8px; }
              td { border: 1px solid #ddd; padding: 8px; }
              tr:nth-child(even) { background-color: #f2f2f2; }
              .report-header { margin-bottom: 20px; }
              .report-filters { margin-bottom: 15px; font-size: 14px; }
              .achievement-achieved { color: green; }
              .achievement-not-achieved { color: red; }
            </style>
          </head>
          <body>
            <div class="report-header">
              <h1>Order Progress Report</h1>
              <div class="report-filters">
                <p><strong>PO Number:</strong> ${filters.poNumber}</p>
              </div>
              <p>Generated on: ${dayjs().format("YYYY-MM-DD HH:mm")}</p>
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>PO Number</th>
                  <th>Customer</th>
                  <th>Item</th>
                  <th>Style</th>
                  <th>PO Date</th>
                  <th>Ex-Factory Date</th>
                  <th>PO Quantity</th>
                  <th>Shipped Qty 1</th>
                  <th>Lot Date 1</th>
                  <th>Shipped Qty 2</th>
                  <th>Lot Date 2</th>
                  <th>Balance Qty</th>
                  <th>Achievement</th>
                  <th>Achievement On Time</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
        `;
        
        const achievementClass = response.data.data.achievement === 'ACHIEVED' ? 'achievement-achieved' : 'achievement-not-achieved';
        
        htmlContent += `
          <tr>
            <td>${response.data.data.poNumber}</td>
            <td>${response.data.data.customer.name}</td>
            <td>${response.data.data.item}</td>
            <td>${response.data.data.pStyle}</td>
            <td>${dayjs(response.data.data.poDate).format('YYYY-MM-DD')}</td>
            <td>${dayjs(response.data.data.exFactoryDate).format('YYYY-MM-DD')}</td>
            <td>${response.data.data.poQuantity}</td>
            <td>${response.data.data.shippedQuantity}</td>
            <td>${response.data.data.lotDate1 ? dayjs(response.data.data.lotDate1).format('YYYY-MM-DD') : ''}</td>
            <td>${response.data.data.shippedQuantity2}</td>
            <td>${response.data.data.lotDate2 ? dayjs(response.data.data.lotDate2).format('YYYY-MM-DD') : ''}</td>
            <td>${response.data.data.balanceQuantity}</td>
            <td class="${achievementClass}">${response.data.data.achievement}</td>
            <td>${response.data.data.achievementOnTime}</td>
            <td>${response.data.data.note}</td>
          </tr>
        `;
        
        htmlContent += `
              </tbody>
            </table>
          </body>
          </html>
        `;
        
        // Convert HTML to PDF blob
        const pdfBlob = await getPDFBlob(htmlContent);
        
        // Create FormData and append the PDF blob
        const formData = new FormData();
        formData.append('image', pdfBlob, `Order_Progress_Report_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.pdf`);
        formData.append('userId', selectedEmailCustomer);
        formData.append('type', "Report");
        // Send the email
        const emailResponse = await api.post('inventory/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        if (emailResponse.status === 200) {
          toast.success('Report sent by email successfully');
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        } else {
          toast.error('Failed to send report by email');
        }
      } else {
        toast.error('No data available for the selected PO Number');
      }
    } catch (error) {
      console.error('Error sending report by email:', error);
      toast.error('Failed to send report by email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <Grid container spacing={3} alignItems="center">
      <Grid item xs={12} sm={10}>
        <TextField
          fullWidth
          label="PO Number"
          value={filters.poNumber}
          onChange={(e) => setFilters({ ...filters, poNumber: e.target.value })}
          placeholder="Enter PO Number"
        />
      </Grid>
      
      <Grid item xs={12} sm={2}>
        <Stack spacing={1}>
          <Button
            variant="contained"
            fullWidth
            onClick={() => generateReport('html')}
            disabled={loadingReport || sendingEmail}
            startIcon={<DescriptionIcon />}
            sx={{ 
              height: "38px", 
              backgroundColor: colors.primary, 
              '&:hover': { 
                backgroundColor: colors.primary, 
                opacity: 0.9 
              } 
            }}
          >
            {loadingReport ? <CircularProgress size={24} color="inherit" /> : "View"}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport('pdf')}
            disabled={loadingReport || sendingEmail}
            startIcon={<PictureAsPdfIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            PDF
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport('excel')}
            disabled={loadingReport || sendingEmail}
            startIcon={<FileDownloadIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            Excel
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => generateReport('email')}
            disabled={loadingReport || sendingEmail}
            startIcon={<EmailIcon />}
            sx={{ 
              height: "38px",
              borderColor: colors.primary,
              color: colors.primary,
              '&:hover': { 
                borderColor: colors.primary,
                opacity: 0.9 
              } 
            }}
          >
            Email
          </Button>
        </Stack>
      </Grid>

      <EmailReportDialog
        open={emailDialogOpen}
        handleClose={() => {
          setEmailDialogOpen(false);
          setSelectedEmailCustomer('');
        }}
        selectedCustomer={selectedEmailCustomer}
        setSelectedCustomer={setSelectedEmailCustomer}
        customers={customers}
        isLoading={sendingEmail}
        handleSendEmail={sendReportByEmail}
      />
    </Grid>
  );
};

export default OrderProgressReport;
